import { useEffect } from 'react';
import { BrowserRouter, Navigate, Route, Routes, useLocation, useNavigate } from 'react-router-dom';
import { useUtmCapture } from './hooks/useUtmCapture';

function ScrollToTop() {
  const { pathname } = useLocation();
  useEffect(() => {
    // Direct assignment bypasses CSS scroll-behavior:smooth on <html>
    document.documentElement.scrollTop = 0;
    document.body.scrollTop = 0;
  }, [pathname]);
  return null;
}
import { HelmetProvider } from 'react-helmet-async';
import { I18nProvider }     from './lib/i18n';
import { AuthProvider }     from './contexts/AuthContext';
import { ToastProvider, useToast } from './components/ui/ToastSystem';
import { ErrorBoundary }    from './components/ErrorBoundary';
import { CurrencyProvider } from './contexts/CurrencyContext';
import { PricingProvider }  from './contexts/PricingContext';
import { CookieConsentBanner }  from './components/shared/CookieConsentBanner';
import { PlanChangeNotifier }    from './components/shared/PlanChangeNotifier';
import { ImpersonationBanner }   from './components/admin/ImpersonationBanner';
import { AppWalkthrough }       from './components/native/AppWalkthrough';
import { setupNetworkListener } from './lib/native/network';
import { isNative }             from './lib/platform';

// Layouts
import { PublicLayout }   from './components/layout/PublicLayout';
import { AppLayout }      from './components/layout/AppLayout';
import { AdminAPLayout }  from './components/layout/AdminAPLayout';
import { ProtectedRoute }    from './components/layout/ProtectedRoute';
import { AdminAPGuard }      from './components/layout/AdminAPGuard';
import { ServiceGateOutlet } from './components/shared/ServiceGate';

// Pages, admin
import { AdminDashboardPage }     from './pages/admin/AdminDashboardPage';
import { AdminPricingPage }       from './pages/admin/AdminPricingPage';
import { AdminCouponsPage }       from './pages/admin/AdminCouponsPage';
import { AdminUsersPage }         from './pages/admin/AdminUsersPage';
import { AdminAnalyticsPage }     from './pages/admin/AdminAnalyticsPage';
import { AdminContentPage }       from './pages/admin/AdminContentPage';
import { AdminIntegrationsPage }  from './pages/admin/AdminIntegrationsPage';
import { AdminSettingsPage }      from './pages/admin/AdminSettingsPage';
import { AdminAuditPage }         from './pages/admin/AdminAuditPage';
import { AdminPaymentsPage }      from './pages/admin/AdminPaymentsPage';
import { AdminChargebacksPage }   from './pages/admin/AdminChargebacksPage';
import { AdminGiftCardsPage }     from './pages/admin/AdminGiftCardsPage';
import { AdminFunctionsPage }     from './pages/admin/AdminFunctionsPage';
import { AdminApiUsagePage }      from './pages/admin/AdminApiUsagePage';
import { AdminAlertsPage }        from './pages/admin/AdminAlertsPage';
import { AdminNotificationsPage } from './pages/admin/AdminNotificationsPage';
import { AdminAffiliatesPage }    from './pages/admin/AdminAffiliatesPage';
import { AdminEmailsPage }        from './pages/admin/AdminEmailsPage';
import { AdminFeatureFlagsPage }  from './pages/admin/AdminFeatureFlagsPage';
import { AdminMenuPage }          from './pages/admin/AdminMenuPage';
import { AdminPlanAccessPage }    from './pages/admin/AdminPlanAccessPage';
import { AdminMessagesPage }      from './pages/admin/AdminMessagesPage';
import { AdminReferralPromoPage } from './pages/admin/AdminReferralPromoPage';
import { AdminCompliancePage }   from './pages/admin/AdminCompliancePage';
import { AdminLegalDocsPage }   from './pages/admin/AdminLegalDocsPage';
import { AdminReportsPage }     from './pages/admin/AdminReportsPage';
import { AdminCostsPage }       from './pages/admin/AdminCostsPage';
import { AdminAiMonitoringPage }       from './pages/admin/AdminAiMonitoringPage';
import { AdminDatabaseMonitoringPage } from './pages/admin/AdminDatabaseMonitoringPage';
import { AdminCronJobsPage }           from './pages/admin/AdminCronJobsPage';
import { AdminConversaoPage }          from './pages/admin/AdminConversaoPage';
import { AdminConversaoFreePaidPage }  from './pages/admin/AdminConversaoFreePaidPage';
import { ForecastMetricsPage }         from './pages/admin/ForecastMetrics';
import { SystemAdoptionPage }          from './pages/admin/SystemAdoptionPage';
import { AdminUserDetailPage }         from './pages/admin/AdminUserDetailPage';
import { AdminEmailMetricsPage }       from './pages/admin/AdminEmailMetricsPage';
import { AdminFraudMetricsPage }        from './pages/admin/AdminFraudMetricsPage';
import { AdminDunningPage }             from './pages/admin/AdminDunningPage';
import { CheckoutAnalyticsDashboard }   from './pages/admin/CheckoutAnalyticsDashboard';
import { AdminPerformancePage }          from './pages/admin/AdminPerformancePage';
import { UpdatePaymentPage }          from './pages/settings/UpdatePaymentPage';
import { FiscalDataPage }             from './pages/settings/FiscalDataPage';
import { MyInvoicesPage }             from './pages/billing/MyInvoicesPage';
import { JourneyPage }                 from './pages/JourneyPage';
import { RegisterConfirmation }        from './pages/RegisterConfirmation';
import { MapaPage }                    from './pages/Mapa';
import { FeatureChecklist }            from './pages/FeatureChecklist';
import { AchievementsPage }            from './pages/Achievements';
import { NotificationSettings }        from './pages/NotificationSettings';
import { SynastryInvitePublic }        from './pages/SynastryInvitePublic';
import { SynastryInviteLanding }       from './pages/SynastryInviteLanding';
import { SynastryResult }              from './pages/SynastryResult';
import { SynastryLandingPage }         from './pages/SynastryLandingPage';
import { SavedChartsPage }             from './pages/SavedChartsPage';
import { MySynastriesPage }            from './pages/MySynastriesPage';
import { SynastryCreatePage }          from './pages/SynastryCreatePage';
import { SynastryMetricsPage }         from './pages/admin/SynastryMetricsPage';
import { WelcomeModal }                from './components/onboarding/WelcomeModal';
import { AchievementProvider }         from './components/gamification/AchievementProvider';
import { InstallPrompt }               from './components/pwa/InstallPrompt';

// Layouts, Professional
import { ProfessionalLayout }  from './components/layout/ProfessionalLayout';
import { ProfessionalGuard }   from './components/layout/ProfessionalGuard';
import { ImpersonationProvider } from './contexts/ImpersonationContext';

// Pages, Professional module
import { ProfessionalActivationWizard } from './pages/professional/ProfessionalActivationWizard';
import { ProfessionalDashboard }          from './pages/professional/ProfessionalDashboard';
import { ProfessionalClientList }         from './pages/professional/ProfessionalClientList';
import { ProfessionalClientNew }          from './pages/professional/ProfessionalClientNew';
import { ProfessionalClientProfile }      from './pages/professional/ProfessionalClientProfile';
import { ProfessionalSettings }           from './pages/professional/ProfessionalSettings';
import { ProfessionalHistory }            from './pages/professional/ProfessionalHistory';
import { ProfessionalCSVImport }          from './pages/professional/ProfessionalCSVImport';
import { ProfessionalGenerateAnalysis }   from './pages/professional/ProfessionalGenerateAnalysis';
import { ProfessionalFeedback }           from './pages/professional/ProfessionalFeedback';

// Pages, Admin astrologers sub-module
import { AdminAstrologersPage }         from './pages/admin/astrologers/AdminAstrologersPage';
import { AdminAstrologerDetailPage }    from './pages/admin/astrologers/AdminAstrologerDetailPage';
import { AdminAstrologerClientsPage }   from './pages/admin/astrologers/AdminAstrologerClientsPage';
import { AdminAstrologerAnalysesPage }  from './pages/admin/astrologers/AdminAstrologerAnalysesPage';
import { AdminAstrologerReportPage }    from './pages/admin/astrologers/AdminAstrologerReportPage';
import { AdminAstrologerSettingsPage }  from './pages/admin/astrologers/AdminAstrologerSettingsPage';
import { AdminAstrologerPricingPage }   from './pages/admin/astrologers/AdminAstrologerPricingPage';

// Pages, Public astrologer profile
import { AstrologerPublicPage }         from './pages/AstrologerPublicPage';

// Pages, public
import { HomePage }          from './pages/HomePage';
import { Login }             from './pages/Login';
import { Register }          from './pages/Register';
import { ResetPassword }     from './pages/ResetPassword';
import { Verify2FA }         from './pages/Verify2FA';
import { ProfilePage }       from './pages/ProfilePage';
import { InvitePage }        from './pages/InvitePage';
import { RefLandingPage }    from './pages/RefLandingPage';
import { PricingPage }       from './pages/PricingPage';
import { CheckoutSuccessPage } from './pages/CheckoutSuccessPage';

// Pages, client
import { OnboardingPage }      from './pages/OnboardingPage';
import { DashboardPage }       from './pages/DashboardPage';
import { BirthChartPage }      from './pages/BirthChartPage';
import { ForecastPage }        from './pages/ForecastPage';
import { TarotPage }           from './pages/TarotPage';
import { LenormandPage }       from './pages/LenormandPage';
import { QuestionsPage }       from './pages/QuestionsPage';
import { AstroMatchPage }      from './pages/AstroMatchPage';
import { SynastryPage }        from './pages/SynastryPage';
import { NumerologyPage }      from './pages/NumerologyPage';
import { IChingPage }          from './pages/IChingPage';
import { DreamJournalPage }    from './pages/DreamJournalPage';
import { MoonPhasesPage }      from './pages/MoonPhasesPage';
import { AstrologicalAgenda }  from './pages/AstrologicalAgenda';
import { RetrogradeRadar }     from './pages/RetrogradeRadar';
import { EclipseInsight }      from './pages/EclipseInsight';
import { AstroPet }            from './pages/AstroPet';
import { AstroCoach }          from './pages/AstroCoach';
import { StartAstral }         from './pages/StartAstral';
import { Empresas }            from './pages/Empresas';
import { Astrocartografia }    from './pages/Astrocartografia';
import { QuiromancyPage }      from './pages/QuiromancyPage';
import { HumanDesignPage }     from './pages/HumanDesignPage';
import { VedicAstrologyPage }  from './pages/VedicAstrologyPage';
import { ReadingsPage }        from './pages/ReadingsPage';
import { AstroJournalPage }   from './pages/AstroJournalPage';
import { CreditWalletPage }    from './pages/CreditWalletPage';
import { GiftCardPage }        from './pages/GiftCardPage';
import { ReferralPage }        from './pages/ReferralPage';
import { AffiliatePage }       from './pages/AffiliatePage';
import { SupportPage }         from './pages/SupportPage';
import { NotificationsPage }   from './pages/NotificationsPage';

// Pages, SEO / public content
import { Privacy }             from './pages/Privacy';
import { Terms }               from './pages/Terms';
import { Cancellation }        from './pages/Cancellation';
import { SignPage }            from './pages/SignPage';
import { CompatibilityPage }   from './pages/CompatibilityPage';
import { AnnualForecastPage }  from './pages/AnnualForecastPage';
import { BlogIndex }           from './pages/BlogIndex';
import { BlogPost }            from './pages/BlogPost';
import { ContactPage }         from './pages/ContactPage';
import { ServiceLandingPage }  from './pages/servicos/ServiceLandingPage';

// Pages, SEO landing pages
import { AscendantePage }      from './pages/seo/AscendantePage';
import { MapaNatalPage }       from './pages/seo/MapaNatalPage';
import { TaroPage as TaroSEOPage } from './pages/seo/TaroPage';
import { RetrogradoPage }      from './pages/seo/RetrogradoPage';
import { CompatibilidadeAstrologicaPage } from './pages/seo/CompatibilidadeAstrologicaPage';

// ─── UTM capture (inner component, needs AuthProvider context) ──────────────

function UtmEffects() {
  useUtmCapture();
  return null;
}

// ─── Network listener + deep links (inner component, needs router context) ──

function NativeEffects() {
  const navigate = useNavigate();
  const { showToast } = useToast();

  // Network status
  useEffect(() => {
    let cleanup: (() => void) | null = null;
    setupNetworkListener(connected => {
      if (connected) {
        showToast({ message: 'Conexão restaurada ✦', type: 'success' });
      } else {
        showToast({ message: 'Sem conexão. Mostrando conteúdo em cache ✦', type: 'info' });
      }
    }).then(fn => { cleanup = fn; });
    return () => { cleanup?.(); };
  }, [showToast]);

  // Deep links / Universal Links (native only)
  useEffect(() => {
    if (!isNative) return;
    let handle: { remove: () => void } | null = null;
    import('@capacitor/app').then(({ App: CapApp }) => {
      CapApp.addListener('appUrlOpen', event => {
        const url = new URL(event.url);
        const path = url.pathname + url.search;

        if (path.startsWith('/ref/')) {
          const code = path.replace('/ref/', '');
          navigate(`/referral?code=${code}`);
        } else if (path.startsWith('/checkout/success')) {
          showToast({ message: 'Pagamento confirmado ✦', type: 'success' });
        } else if (path.startsWith('/app/') || path.startsWith('/invite/') || path.startsWith('/dashboard')) {
          navigate(path);
        }
      }).then(l => { handle = l; });
    });
    return () => { handle?.remove(); };
  }, [navigate, showToast]);

  return null;
}

// ─── Root App ────────────────────────────────────────────────────────────────

export default function App() {
  return (
    <I18nProvider>
    <HelmetProvider>
    <ErrorBoundary>
      <AuthProvider>
        <ImpersonationProvider>
        <UtmEffects />
        <CurrencyProvider>
          <PricingProvider>
            <ToastProvider>
            <AchievementProvider>
              <BrowserRouter>
                <ScrollToTop />
                <NativeEffects />
                <AppWalkthrough />
                <ImpersonationBanner />
                <CookieConsentBanner />
                <PlanChangeNotifier />
                <WelcomeModal />
                <InstallPrompt />
                <Routes>

                  {/* ─────────────────────────────────────────
                      ZONE 1, Public (no auth)
                      ───────────────────────────────────────── */}
                  <Route element={<PublicLayout />}>
                    <Route path="/"               element={<HomePage />} />
                    <Route path="/pricing"        element={<PricingPage />} />
                    <Route path="/servicos/:slug" element={<ServiceLandingPage />} />
                    <Route path="/blog"           element={<BlogIndex />} />
                    <Route path="/blog/:slug"     element={<BlogPost />} />
                  </Route>

                  {/* Auth pages (no layout chrome) */}
                  <Route path="/login"               element={<Login />} />
                  <Route path="/login/verify-2fa"    element={<Verify2FA />} />
                  <Route path="/register"            element={<Register />} />
                  <Route path="/register/confirm"    element={<RegisterConfirmation />} />
                  <Route path="/reset-password"      element={<ResetPassword />} />

                  {/* Legal, English and Portuguese aliases */}
                  <Route path="/privacy"          element={<Privacy />} />
                  <Route path="/privacidade"      element={<Privacy />} />
                  <Route path="/terms"            element={<Terms />} />
                  <Route path="/termos"           element={<Terms />} />
                  <Route path="/cancellation"     element={<Cancellation />} />
                  <Route path="/cancelamento"     element={<Cancellation />} />

                  {/* Contact */}
                  <Route path="/contato"  element={<ContactPage />} />
                  <Route path="/contact"  element={<ContactPage />} />

                  {/* SEO content pages */}
                  <Route path="/signos/:signo"                element={<SignPage />} />
                  <Route path="/compatibilidade/:par"         element={<CompatibilityPage />} />
                  <Route path="/previsao-2027/:signo"         element={<AnnualForecastPage />} />
                  <Route path="/ascendente"                           element={<AscendantePage />} />
                  <Route path="/mapa-natal"                           element={<MapaNatalPage />} />
                  <Route path="/taro"                                 element={<TaroSEOPage />} />
                  <Route path="/retrogrado"                           element={<RetrogradoPage />} />
                  <Route path="/compatibilidade-astrologica"          element={<CompatibilidadeAstrologicaPage />} />

                  {/* Public special routes */}
                  <Route path="/invite/:token"               element={<InvitePage />} />
                  <Route path="/ref/:code"                   element={<RefLandingPage />} />
                  <Route path="/checkout/success"            element={<CheckoutSuccessPage />} />
                  <Route path="/convite/sinastria"           element={<SynastryInvitePublic />} />

                  {/* ─────────────────────────────────────────
                      ZONE 2, Client area (requires auth)
                      ───────────────────────────────────────── */}
                  <Route element={<ProtectedRoute />}>
                    {/* Onboarding (full-screen, no sidebar) */}
                    <Route path="/onboarding" element={<OnboardingPage />} />

                    {/* Client layout (sidebar + bottom tabs) */}
                    <Route element={<AppLayout />}>
                      {/* Non-gated utility pages */}
                      <Route path="/dashboard"                    element={<DashboardPage />} />
                      <Route path="/readings"                     element={<ReadingsPage />} />
                      <Route path="/astro-journal"                element={<AstroJournalPage />} />
                      <Route path="/wallet"                       element={<CreditWalletPage />} />
                      <Route path="/gift-card"                    element={<GiftCardPage />} />
                      <Route path="/referral"                     element={<ReferralPage />} />
                      <Route path="/afiliado"                     element={<AffiliatePage />} />
                      <Route path="/profile"                      element={<ProfilePage />} />
                      <Route path="/suporte"                      element={<SupportPage />} />
                      <Route path="/notificacoes"                 element={<NotificationsPage />} />
                      <Route path="/empresas/*"                   element={<Empresas />} />
                      <Route path="/atlas-astral"                 element={<Navigate to="/astrocartografia" replace />} />
                      <Route path="/descobrir"                    element={<FeatureChecklist />} />
                      <Route path="/conquistas"                   element={<AchievementsPage />} />
                      <Route path="/configuracoes/notificacoes"   element={<NotificationSettings />} />
                      <Route path="/configuracoes/pagamento"      element={<UpdatePaymentPage />} />
                      <Route path="/configuracoes/fiscal"         element={<FiscalDataPage />} />
                      <Route path="/faturas"                      element={<MyInvoicesPage />} />

                      {/* Service pages, gated by admin plan-access matrix */}
                      <Route element={<ServiceGateOutlet />}>
                        <Route path="/birth-chart"        element={<BirthChartPage />} />
                        <Route path="/forecast"           element={<ForecastPage />} />
                        <Route path="/synastry"           element={<SynastryPage />} />
                        <Route path="/questions"          element={<QuestionsPage />} />
                        <Route path="/astromatch"         element={<AstroMatchPage />} />
                        <Route path="/tarot"              element={<TarotPage />} />
                        <Route path="/lenormand"          element={<LenormandPage />} />
                        <Route path="/numerology"         element={<NumerologyPage />} />
                        <Route path="/i-ching"            element={<IChingPage />} />
                        <Route path="/dream-journal"      element={<DreamJournalPage />} />
                        <Route path="/moon-phases"        element={<MoonPhasesPage />} />
                        <Route path="/agenda"             element={<AstrologicalAgenda />} />
                        <Route path="/retrograde-radar"   element={<RetrogradeRadar />} />
                        <Route path="/retrogrado"         element={<Navigate to="/retrograde-radar" replace />} />
                        <Route path="/eclipses"           element={<EclipseInsight />} />
                        <Route path="/astro-pet"          element={<AstroPet />} />
                        <Route path="/astro-coach"        element={<AstroCoach />} />
                        <Route path="/rota-profissional"  element={<StartAstral />} />
                        <Route path="/astrocartografia"   element={<Astrocartografia />} />
                        <Route path="/quiromancy"         element={<QuiromancyPage />} />
                        <Route path="/human-design"       element={<HumanDesignPage />} />
                        <Route path="/vedic-astrology"    element={<VedicAstrologyPage />} />
                      </Route>

                      {/* Professional module, inside AppLayout so main sidebar shows */}
                      <Route element={<ProfessionalGuard />}>
                        <Route element={<ProfessionalLayout />}>
                          <Route path="/profissional/portal"             element={<ProfessionalDashboard />} />
                          <Route path="/profissional/dashboard"          element={<Navigate to="/profissional/portal" replace />} />
                          <Route path="/profissional/clientes"           element={<ProfessionalClientList />} />
                          <Route path="/profissional/clientes/novo"      element={<ProfessionalClientNew />} />
                          <Route path="/profissional/clientes/importar"  element={<ProfessionalCSVImport />} />
                          <Route path="/profissional/clientes/:clientId" element={<ProfessionalClientProfile />} />
                          <Route path="/profissional/gerar-analise"      element={<ProfessionalGenerateAnalysis />} />
                          <Route path="/profissional/historico"          element={<ProfessionalHistory />} />
                          <Route path="/profissional/perfil"             element={<ProfessionalSettings />} />
                          <Route path="/profissional/configuracoes"      element={<ProfessionalSettings />} />
                          <Route path="/profissional/feedback"           element={<ProfessionalFeedback />} />
                          <Route path="/profissional"                    element={<Navigate to="/profissional/portal" replace />} />
                        </Route>
                      </Route>
                    </Route>
                  </Route>

                  {/* Activation wizard, no layout chrome */}
                  <Route element={<ProtectedRoute />}>
                    <Route element={<ProfessionalGuard />}>
                      <Route path="/profissional/ativar" element={<ProfessionalActivationWizard />} />
                    </Route>
                  </Route>

                  {/* ─────────────────────────────────────────
                      ZONE 3, Admin panel
                      ───────────────────────────────────────── */}
                  <Route element={<ProtectedRoute />}>
                    <Route element={<AdminAPGuard />}>
                      <Route element={<AdminAPLayout />}>
                        {/* Existing admin routes */}
                        <Route path="/admin-ap/dashboard"    element={<AdminDashboardPage />} />
                        <Route path="/admin-ap/pricing"      element={<AdminPricingPage />} />
                        <Route path="/admin-ap/coupons"      element={<AdminCouponsPage />} />
                        <Route path="/admin-ap/cupons"       element={<AdminCouponsPage />} />
                        <Route path="/admin-ap/analytics"    element={<AdminAnalyticsPage />} />
                        <Route path="/admin-ap/users"        element={<AdminUsersPage />} />
                        <Route path="/admin-ap/content"      element={<AdminContentPage />} />
                        <Route path="/admin-ap/integrations" element={<AdminIntegrationsPage />} />
                        <Route path="/admin-ap/settings"     element={<AdminSettingsPage />} />
                        <Route path="/admin-ap/audit"        element={<AdminAuditPage />} />
                        {/* New admin routes */}
                        <Route path="/admin-ap/pagamentos"   element={<AdminPaymentsPage />} />
                        <Route path="/admin-ap/chargebacks"  element={<AdminChargebacksPage />} />
                        <Route path="/admin-ap/gift-cards"   element={<AdminGiftCardsPage />} />
                        <Route path="/admin-ap/functions"    element={<AdminFunctionsPage />} />
                        <Route path="/admin-ap/api-usage"    element={<AdminApiUsagePage />} />
                        <Route path="/admin-ap/alertas"      element={<AdminAlertsPage />} />
                        <Route path="/admin-ap/notificacoes" element={<AdminNotificationsPage />} />
                        <Route path="/admin-ap/afiliados"      element={<AdminAffiliatesPage />} />
                        <Route path="/admin-ap/emails"         element={<AdminEmailsPage />} />
                        <Route path="/admin-ap/feature-flags"  element={<AdminFeatureFlagsPage />} />
                        <Route path="/admin-ap/menu"            element={<AdminMenuPage />} />
                        <Route path="/admin-ap/plan-access"    element={<AdminPlanAccessPage />} />
                        <Route path="/admin-ap/mensagens"      element={<AdminMessagesPage />} />
                        <Route path="/admin-ap/suporte"        element={<Navigate to="/admin-ap/mensagens" replace />} />
                        <Route path="/admin-ap/referral-promo" element={<AdminReferralPromoPage />} />
                        <Route path="/admin-ap/compliance"    element={<AdminCompliancePage />} />
                        <Route path="/admin-ap/legal-docs"    element={<AdminLegalDocsPage />} />
                        <Route path="/admin-ap/reports"       element={<AdminReportsPage />} />
                        <Route path="/admin-ap/costs"         element={<AdminCostsPage />} />
                        <Route path="/admin-ap/conversao"           element={<AdminConversaoPage />} />
                        <Route path="/admin-ap/conversao-free-paid" element={<AdminConversaoFreePaidPage />} />
                        <Route path="/admin-ap/monitoring/ai"       element={<AdminAiMonitoringPage />} />
                        <Route path="/admin-ap/monitoring/database" element={<AdminDatabaseMonitoringPage />} />
                        <Route path="/admin-ap/monitoring/cron"     element={<AdminCronJobsPage />} />
                        <Route path="/admin-ap/forecast-metrics"      element={<ForecastMetricsPage />} />
                        <Route path="/admin-ap/sinastria-metricas"   element={<SynastryMetricsPage />} />
                        <Route path="/admin-ap/system-adoption"      element={<SystemAdoptionPage />} />
                        <Route path="/admin-ap/users/:id"            element={<AdminUserDetailPage />} />
                        <Route path="/admin-ap/email-metrics"        element={<AdminEmailMetricsPage />} />
                        <Route path="/admin-ap/fraud-metrics"        element={<AdminFraudMetricsPage />} />
                        <Route path="/admin-ap/dunning"              element={<AdminDunningPage />} />
                        <Route path="/admin-ap/checkout-analytics"   element={<CheckoutAnalyticsDashboard />} />
                        <Route path="/admin-ap/performance"           element={<AdminPerformancePage />} />
                        {/* Redirect /admin-ap → dashboard */}
                        <Route path="/admin-ap" element={<Navigate to="/admin-ap/dashboard" replace />} />
                      </Route>
                    </Route>
                  </Route>

                  {/* ─────────────────────────────────────────
                      ZONE 4b, Admin: Astrologers sub-module
                      ───────────────────────────────────────── */}
                  <Route element={<ProtectedRoute />}>
                    <Route element={<AdminAPGuard />}>
                      <Route element={<AdminAPLayout />}>
                        <Route path="/admin-ap/astrologos"              element={<AdminAstrologersPage />} />
                        <Route path="/admin-ap/astrologos/:id"          element={<AdminAstrologerDetailPage />} />
                        <Route path="/admin-ap/astrologos/clientes"     element={<AdminAstrologerClientsPage />} />
                        <Route path="/admin-ap/astrologos/analises"     element={<AdminAstrologerAnalysesPage />} />
                        <Route path="/admin-ap/astrologos/relatorio"    element={<AdminAstrologerReportPage />} />
                        <Route path="/admin-ap/astrologos/configuracoes" element={<AdminAstrologerSettingsPage />} />
                        <Route path="/admin-ap/astrologos/planos"       element={<AdminAstrologerPricingPage />} />
                      </Route>
                    </Route>
                  </Route>

                  {/* Public astrologer profile page */}
                  <Route path="/astrologo/:slug" element={<AstrologerPublicPage />} />

                  {/* Sinastria public routes (no auth required) */}
                  <Route path="/sinastria/inicio"          element={<SynastryLandingPage />} />
                  <Route path="/sinastria/convite/:token"  element={<SynastryInviteLanding />} />

                  {/* Sinastria authenticated routes */}
                  <Route path="/sinastria/criar"           element={<SynastryCreatePage />} />
                  <Route path="/sinastria/:id"             element={<SynastryResult />} />
                  <Route path="/meus-mapas"                element={<SavedChartsPage />} />
                  <Route path="/minhas-sinastrias"         element={<MySynastriesPage />} />
                  <Route path="/jornada"                   element={<JourneyPage />} />

                  {/* Portuguese URL aliases for internal pages, redirect to canonical routes */}
                  <Route path="/mapa-astral"  element={<Navigate to="/birth-chart" replace />} />
                  <Route path="/sinastria"    element={<Navigate to="/sinastria/inicio" replace />} />
                  <Route path="/perguntas"    element={<Navigate to="/questions" replace />} />
                  <Route path="/previsao"     element={<Navigate to="/forecast" replace />} />
                  <Route path="/mapa"         element={<MapaPage />} />
                  <Route path="/planos"       element={<Navigate to="/pricing" replace />} />

                  <Route path="*" element={<Navigate to="/" replace />} />
                </Routes>
              </BrowserRouter>
            </AchievementProvider>
            </ToastProvider>
          </PricingProvider>
        </CurrencyProvider>
        </ImpersonationProvider>
      </AuthProvider>
    </ErrorBoundary>
    </HelmetProvider>
    </I18nProvider>
  );
}

